import type { Contract } from './contract';

/**
 * A vendor's XML bill, read as the JSON every other step works on. The data plane reads it the
 * same way (`XmlBills.java`); both are held to the cases in `__fixtures__/xml`. An element is a
 * key, a repeated element a list, an attribute `@name`, text beside attributes or elements
 * `#text`, the root unwrapped. Every value is text until the endpoint's rules type it.
 */

interface XmlElement {
  name: string;
  attributes: [string, string][];
  children: XmlElement[];
  text: string;
}

const ENTITIES: Record<string, string> = { lt: '<', gt: '>', amp: '&', quot: '"', apos: "'" };

const decode = (text: string): string =>
  text.replace(/&(#x[0-9a-fA-F]+|#\d+|\w+);/g, (_whole, ref: string) => {
    if (ref.startsWith('#x')) return String.fromCodePoint(parseInt(ref.slice(2), 16));
    if (ref.startsWith('#')) return String.fromCodePoint(parseInt(ref.slice(1), 10));
    if (ref in ENTITIES) return ENTITIES[ref];
    throw new Error(`The entity &${ref}; is not allowed.`);
  });

const parse = (xml: string): XmlElement => {
  if (/<!DOCTYPE/i.test(xml)) throw new Error('DOCTYPE is not allowed in a bill.');
  let at = 0;
  const fail = (what: string): never => {
    const line = xml.slice(0, at).split('\n').length;
    throw new Error(`line ${line}: ${what}`);
  };
  const skipMisc = () => {
    for (;;) {
      const rest = xml.slice(at);
      const space = rest.match(/^\s+/);
      const close = xml.startsWith('<?', at) ? '?>' : xml.startsWith('<!--', at) ? '-->' : undefined;
      if (space) at += space[0].length;
      else if (close) {
        const stop = xml.indexOf(close, at);
        if (stop < 0) fail(close === '?>' ? 'unclosed <?' : 'unclosed comment');
        at = stop + close.length;
      } else return;
    }
  };

  const element = (): XmlElement => {
    const open = xml.slice(at).match(/^<([A-Za-z_][\w.:-]*)/);
    if (!open) return fail('an element was expected');
    const node: XmlElement = { name: open[1], attributes: [], children: [], text: '' };
    at += open[0].length;
    for (;;) {
      const attribute = xml.slice(at).match(/^\s+([A-Za-z_][\w.:-]*)\s*=\s*("([^"]*)"|'([^']*)')/);
      if (!attribute) break;
      node.attributes.push([attribute[1], decode(attribute[3] ?? attribute[4] ?? '')]);
      at += attribute[0].length;
    }
    const end = xml.slice(at).match(/^\s*(\/?)>/);
    if (!end) return fail(`<${node.name}> is not closed properly`);
    at += end[0].length;
    if (end[1]) return node;

    for (;;) {
      if (at >= xml.length) return fail(`<${node.name}> is never closed`);
      if (xml.startsWith('</', at)) {
        const close = xml.slice(at).match(/^<\/([A-Za-z_][\w.:-]*)\s*>/);
        if (!close || close[1] !== node.name) return fail(`</${node.name}> was expected`);
        at += close[0].length;
        return node;
      }
      if (xml.startsWith('<![CDATA[', at)) {
        const stop = xml.indexOf(']]>', at);
        if (stop < 0) return fail('unclosed CDATA');
        node.text += xml.slice(at + 9, stop);
        at = stop + 3;
      } else if (xml.startsWith('<!--', at)) {
        const stop = xml.indexOf('-->', at);
        if (stop < 0) return fail('unclosed comment');
        at = stop + 3;
      } else if (xml.startsWith('<', at)) node.children.push(element());
      else {
        const stop = xml.indexOf('<', at);
        node.text += decode(xml.slice(at, stop < 0 ? xml.length : stop));
        at = stop < 0 ? xml.length : stop;
      }
    }
  };

  skipMisc();
  const root = element();
  skipMisc();
  if (at < xml.length) fail('there is content after the root element');
  return root;
};

const valueOf = (node: XmlElement): unknown => {
  const text = node.text.trim();
  const attributes = node.attributes.filter(([name]) => !name.startsWith('xmlns'));
  if (attributes.length === 0 && node.children.length === 0) return text;
  const out: Record<string, unknown> = {};
  for (const [name, value] of attributes) out[`@${name}`] = value;
  const named = new Map<string, unknown[]>();
  for (const child of node.children) named.set(child.name, [...(named.get(child.name) ?? []), valueOf(child)]);
  for (const [name, values] of named) out[name] = values.length === 1 ? values[0] : values;
  if (text) out['#text'] = text;
  return out;
};

/**
 * The bill as JSON. An endpoint that takes a list of bills reads a root holding only elements of
 * one name, `<Bills><Bill/>…</Bills>`, as that list, even of one; any other root is one bill.
 */
export const xmlToJson = (xml: string, list = false): unknown => {
  const root = parse(xml);
  if (
    list &&
    root.children.length > 0 &&
    root.attributes.length === 0 &&
    !root.text.trim() &&
    root.children.every((child) => child.name === root.children[0].name)
  )
    return root.children.map(valueOf);
  return valueOf(root);
};

const isNode = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const apply = (node: unknown, parts: string[], type: string): void => {
  if (!isNode(node) || parts.length === 0) return;
  const [part, ...rest] = parts;
  const list = part.endsWith('[]');
  const key = list ? part.slice(0, -2) : part;
  const held = node[key];
  if (held === undefined || held === null) return;
  if (list || (rest.length === 0 && type === 'array')) {
    const items = Array.isArray(held) ? held : [held];
    node[key] = items;
    if (rest.length) items.forEach((item) => apply(item, rest, type));
    return;
  }
  if (rest.length) {
    apply(held, rest, type);
    return;
  }
  if (typeof held !== 'string') return;
  const text = held.trim();
  if (
    (type === 'number' && /^-?\d+(\.\d+)?([eE][-+]?\d+)?$/.test(text)) ||
    (type === 'integer' && /^-?\d+$/.test(text))
  )
    node[key] = Number(text);
  else if (type === 'boolean' && /^(true|false)$/i.test(text)) node[key] = text.toLowerCase() === 'true';
};

/** The fields the rules type as lists, numbers or flags, made so; anything else stays text. */
export const typedBill = (bill: unknown, rules: (Contract | undefined)[]): unknown => {
  const types = new Map<string, string>();
  for (const contract of rules)
    for (const field of contract?.fields ?? [])
      if (['array', 'number', 'integer', 'boolean'].includes(field.type) && !types.has(field.path))
        types.set(field.path, field.type);
  if (types.size === 0) return bill;
  const typeOne = (document: unknown) => {
    const out = structuredClone(document);
    [...types.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([path, type]) => apply(out, path.split('.'), type));
    return out;
  };
  return Array.isArray(bill) ? bill.map(typeOne) : typeOne(bill);
};

/** A bill as the platform works on it: JSON as it came, XML read and typed the same way the door does. */
export const readBill = (
  payload: string,
  format: 'json' | 'xml' | undefined,
  options: { list?: boolean; rules?: (Contract | undefined)[] } = {},
): unknown =>
  format === 'xml'
    ? typedBill(xmlToJson(payload, options.list), options.rules ?? [])
    : (JSON.parse(payload) as unknown);

/** One of an endpoint's samples as a bill, read the way the endpoint takes its bills. */
export const billOf = (route: { format?: 'xml'; isBatch?: boolean } | undefined, payload: string): unknown =>
  readBill(payload, route?.format, { list: route?.isBatch === true });
